import { html, type TemplateResult } from 'lit';
import { safeHttpUrl } from '../utils/is-safe-http-url';
import { msg } from '../localization/config';
import type { FormAlert } from '../types/appointment-booking.types';

const blocks = {
    personalInfo: ({ lang, namePattern }: { lang: string; namePattern: string }): TemplateResult => html`
        <div class="m-appointment-request__input-wrapper">
            <der-ui-radio-group
                id="genderGroup"
                name="ContactGender"
                label="${msg('salutation')}"
                language="${lang}"
                ?required="${true}"
            >
                <der-ui-radio-button value="2" label="${msg('genders.female')}"></der-ui-radio-button>
                <der-ui-radio-button value="1" label="${msg('genders.male')}"></der-ui-radio-button>
                <der-ui-radio-button value="3" label="${msg('genders.other')}"></der-ui-radio-button>
            </der-ui-radio-group>
        </div>
        <div class="m-appointment-request__input-wrapper m-appointment-request__multiple-inputs-wrapper">
            <der-ui-input
                label="${msg('fields.firstName')}"
                name="ContactFirstName"
                ?required="${true}"
                pattern="${namePattern}"
            ></der-ui-input>
            <der-ui-input
                label="${msg('fields.lastName')}"
                name="ContactLastName"
                ?required="${true}"
                pattern="${namePattern}"
            ></der-ui-input>
        </div>
    `,

    phoneEmail: ({ lang, emailPattern }: { lang: string; emailPattern: string }): TemplateResult => html`
        <div class="m-appointment-request__input-wrapper m-appointment-request__multiple-inputs-wrapper">
            <der-ui-phone-input
                lang="${lang}"
                label="${msg('fields.phone')}"
                name="ContactPhone"
                input-placeholder=""
                ?required="${true}"
            ></der-ui-phone-input>
            <der-ui-input
                label="${msg('fields.email')}"
                name="ContactEmail"
                inputmode="email"
                pattern="${emailPattern}"
                ?required="${true}"
                type="email"
            ></der-ui-input>
        </div>
    `,

    emailConfirmation: (): TemplateResult => html`
        <div class="m-appointment-request__email-confirmation">
            <label for="emailConfirmation">${msg('fields.emailConfirmation')}</label>
            <input
                type="email"
                id="emailConfirmation"
                name="ContactEmailConfirmation"
                tabindex="-1"
                autocomplete="new-password"
            />
        </div>
    `,

    description: (): TemplateResult => html`
        <div class="m-appointment-request__input-wrapper">
            <der-ui-textarea
                label="${msg('fields.description')}"
                name="OpportunityDescription"
                ?required="${true}"
                maxlength="2000"
            ></der-ui-textarea>
        </div>
    `,

    methodOfContact: ({ lang }: { lang: string }): TemplateResult => html`
        <div class="m-appointment-request__input-wrapper">
            <der-ui-radio-group
                name="OpportunityPreferredMethodOfContact"
                label="${msg('fields.methodOfContact')}"
                language="${lang}"
                ?required="${true}"
            >
                <der-ui-radio-button value="Email" label="${msg('fields.methodOfContact.email')}"></der-ui-radio-button>
                <der-ui-radio-button value="Phone" label="${msg('fields.methodOfContact.phone')}"></der-ui-radio-button>
            </der-ui-radio-group>
        </div>
    `,

    costConfirmation: (): TemplateResult => html`
        <div class="m-appointment-request__checkbox-wrapper">
            <der-ui-checkbox name="OpportunityCostConfirmation" ?required="${true}"></der-ui-checkbox>
            <div class="m-appointment-request__checkbox-wrapper__text">
                ${msg('fields.opportunityCostConfirmation')}
            </div>
        </div>
    `,

    newsletter: (newsletter: string): TemplateResult | '' =>
        newsletter.trim() !== ''
            ? html`<div class="m-appointment-request__checkbox-wrapper">
                  <der-ui-checkbox name="ContactNewsletter"></der-ui-checkbox>
                  <div class="m-appointment-request__checkbox-wrapper__text">
                      ${msg('fields.newsletterSubscription')}
                  </div>
              </div>`
            : '',

    alert: ({ type, message }: FormAlert): TemplateResult => html`
        <div class="m-appointment-request__alert-wrapper">
            <der-ui-alert type="${type}" size="large"> ${message} </der-ui-alert>
        </div>
    `,

    dataProtection: ({ dataProtectionLink }: { dataProtectionLink: string }): TemplateResult => {
        const href = safeHttpUrl(dataProtectionLink);
        const linkText = msg('fields.requestDataProtectionLink.linkText');

        // Lit binds the href as an escaped attribute value, and safeHttpUrl
        // guarantees a safe http(s) scheme. Unsafe/empty URLs fall back to
        // plain text so they can never become a clickable link.
        const link = href ? html`<a href=${href} target="_blank" rel="noopener noreferrer">${linkText}</a>` : linkText;

        return html`
            <div class="m-appointment-request__data-protection-wrapper-wrapper">
                ${msg('fields.requestDataProtectionLink.before')}${link}${msg('fields.requestDataProtectionLink.after')}
            </div>
        `;
    },
};

type BaseVariantData = {
    lang: string;
    namePattern: string;
    emailPattern: string;
    newsletter: string;
    formAlert?: FormAlert;
    dataProtectionLink: string;
    onValidityChange: (event: Event) => void;
    onSubmit: (event: Event) => void;
};

const renderFormFields = (data: BaseVariantData, includeCostConfirmation: boolean): TemplateResult => html`
    <div class="m-appointment-request__form">
        <div class="m-appointment-request__inputs-wrapper">
            ${blocks.personalInfo(data)} ${blocks.phoneEmail(data)} ${blocks.emailConfirmation()}
            ${blocks.description()} ${blocks.methodOfContact(data)} ${blocks.newsletter(data.newsletter)}
            ${includeCostConfirmation ? blocks.costConfirmation() : ''}
            ${data.formAlert ? blocks.alert(data.formAlert) : ''} ${blocks.dataProtection(data)}
        </div>
    </div>
`;

export const variantRegistry = {
    default: (data: BaseVariantData): TemplateResult => html`
        <form @der.validity-change=${data.onValidityChange} @der.submit=${data.onSubmit}>
            ${renderFormFields(data, false)}
        </form>
    `,

    costs: (data: BaseVariantData): TemplateResult => html`
        <form @der.validity-change=${data.onValidityChange} @der.submit=${data.onSubmit}>
            ${renderFormFields(data, true)}
        </form>
    `,
};
